import { createRootRoute, Link, Outlet } from '@tanstack/react-router';
import { Languages, LogOut, Menu, User, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { useAppointments } from '@/hooks/use-appointments';
import { cn } from '@/lib/utils';
import { AuthProvider, useAuth } from '@/providers/auth-provider';
import { ImageCacheProvider } from '@/providers/image-cache-provider';

function RootComponent() {
  return (
    <AuthProvider>
      <ImageCacheProvider>
        <RootLayout />
      </ImageCacheProvider>
    </AuthProvider>
  );
}

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
}

function NavLink({ to, children, badge }: NavLinkProps) {
  return (
    <NavigationMenuItem>
      <Link to={to} className="[&.active]:font-bold">
        <NavigationMenuLink className={navigationMenuTriggerStyle()}>
          <span className="flex items-center gap-2">
            {children}
            {badge}
          </span>
        </NavigationMenuLink>
      </Link>
    </NavigationMenuItem>
  );
}

function RootLayout() {
  const { t, i18n } = useTranslation();
  const { session, signOut, status } = useAuth();
  const { data: appointmentsData } = useAppointments();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const allAppointments = appointmentsData?.appointments || [];
  const now = new Date();

  const unassignedAppointmentsCount = useMemo(() => {
    return allAppointments.filter(
      (appointment) => appointment.status === 'unassigned' && new Date(appointment.startTime) >= now,
    ).length;
  }, [allAppointments, now]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'de' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleSignOut = () => {
    signOut();
  };

  const navItems = [
    { to: '/', label: t('nav.home') },
    {
      to: '/needs-driver',
      label: t('nav.needsDriver'),
      badge: unassignedAppointmentsCount > 0 && (
        <Badge variant="destructive" className="text-xs">
          {unassignedAppointmentsCount}
        </Badge>
      ),
    },
    { to: '/appointments', label: t('nav.appointments') },
    { to: '/analytics', label: t('nav.analytics') },
    { to: '/users', label: t('nav.users') },
    { to: '/settings', label: t('nav.settings') },
    { to: '/help', label: t('nav.help') },
    ...(import.meta.env.DEV ? [{ to: '/sentry-test', label: t('nav.sentryTest') }] : []),
  ];

  return (
    <>
      <header className="border-b">
        <div className="container mx-auto px-3 py-3 md:px-4 md:py-4 flex justify-between items-center">
          {/* Desktop Navigation */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} badge={item.badge}>
                  {item.label}
                </NavLink>
              ))}
            </NavigationMenuList>
          </NavigationMenu>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="h-10 w-10"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={toggleLanguage} className="flex items-center gap-2">
              <Languages className="h-4 w-4" />
              {i18n.language === 'en' ? 'DE' : 'EN'}
            </Button>

            {status === 'authenticated' && session && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md border">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{session.user.name || session.user.email}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={handleSignOut} className="gap-2">
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('auth.signOut')}</span>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="absolute top-0 left-0 right-0 bg-background border-b md:hidden z-50 shadow-lg">
            <div className="container mx-auto px-4 py-3">
              <nav className="flex flex-col space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
                      '[&.active]:bg-accent/50 [&.active]:text-accent-foreground',
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span>{item.label}</span>
                    {item.badge}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>

      <main className="container mx-auto px-3 py-4 md:px-4 md:py-6">
        <Outlet />
      </main>
    </>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
